/**
 * "Back to X" link at the top of a sub-page.
 *
 * The prototype hardcoded each of these to a single destination — the License,
 * Top-up and Products pages all said "Back to Settings" — so reaching the License
 * page from the sidebar and clicking back sent the user to a page they had never
 * visited. Phase 1 patched that with a manual view-history stack.
 *
 * With the router in place the browser's own history is the source of truth, so
 * `history` mode simply calls navigate(-1). `fallbackTo` covers a deep link opened
 * in a fresh tab, where there is no history entry to go back to.
 *
 * The cost of history mode is the label: navigate(-1) cannot know what the previous
 * entry was, so it can only say "Back". `parent` mode exists for pages whose parent
 * is fixed — a support topic is always under the Support Center — and keeps the
 * monolith's named label.
 */
import { ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { c } from '../../styles/theme';

export interface BackLinkProps {
  /** Destination path. Used on every click in `parent` mode. */
  fallbackTo: string;
  /** Name of the destination, e.g. "Settings". */
  fallbackLabel: string;
  /**
   * `history` (default) pops the browser stack and labels itself "Back".
   * `parent` always goes to `fallbackTo` and labels itself "Back to {label}".
   */
  mode?: 'history' | 'parent';
}

export default function BackLink({ fallbackTo, fallbackLabel, mode = 'history' }: BackLinkProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // location.key is 'default' only for the first entry in this tab's history, so
  // there is nothing to go back to and navigate(-1) would leave the app.
  const canGoBack = mode === 'history' && location.key !== 'default';

  return (
    <button
      type="button"
      onClick={() => (canGoBack ? navigate(-1) : navigate(fallbackTo))}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginBottom: 14,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
      }}
    >
      <ArrowLeft size={14} color={c.text2} />
      <span style={{ fontSize: 12, color: c.text2 }}>
        Back{canGoBack ? '' : ` to ${fallbackLabel}`}
      </span>
    </button>
  );
}
